import React, {useEffect, useState, useCallback} from 'react';
import {Card, Col, Form, Row} from 'react-bootstrap';
import TableComponent from '@/components/table/Table';
import axios from 'axios';
import CellLoader from '@/components/loading/CellLoader';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {Tooltip} from '@/components/tooltips';
import Icon from '@/components/icons/Icon';
import PrioritiseButton from '@/components/utils/PrioritiseButton';
import {getEventEvaluationUrl} from './utils/evaluationsUrl';
import i18n from 'i18n';
import {useStoreState} from 'easy-peasy';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface Evaluation {
    id: string;
    key?: string;
    type: string;
    label: string;
    value: any;
    description?: string;
    priority?: boolean;
    _restricted?: boolean;
    channelComp?: {
        avg: number;
    };
    eventTypeComp?: {
        avg: number;
    };
    percentage_same_value?: number;
    percentage_same_topic?: number;
}

interface ProcessedEvaluation {
    id: string;
    key?: string;
    label: string;
    value: React.ReactNode;
    channelAverage?: number;
    channelComp?: number;
    eventTypeAverage?: number;
    eventTypeComp?: number;
    description?: string;
    insight: string;
    priority: boolean;
    _restricted?: boolean;
}

interface AppConfig {
    EVALUATION_TYPE_BEFORE_X_DAYS_THRESHOLD: number;
}

interface AppConfigs {
    items: AppConfig;
}

interface StoreState {
    appConfigs: AppConfigs;
}

interface EventEvaluationsTabProps {
    eventId: string;
    isEvaluationsTabClicked: boolean;
}

const setSearchQueryState = async (setSearchQuery: (value: string) => void, value: string): Promise<void> => {
    setSearchQuery(value);
};
const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const EventEvaluationsTab: React.FC<EventEvaluationsTabProps> = React.memo(
    ({eventId, isEvaluationsTabClicked}) => {
        const appConfig = useStoreState((state: StoreState) => state.appConfigs.items);
        const [loading, setLoading] = useState<boolean>(true);
        const [sortedEvaluations, setSortedEvaluations] = useState<Evaluation[]>([]);
        const [searchQuery, setSearchQuery] = useState<string>('');

        const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
            setSearchQueryStateDebounced(setSearchQuery, event.target.value);
        };

        const fetchEvaluations = useCallback(async (): Promise<void> => {
            if (!isEvaluationsTabClicked) {return;}

            setLoading(true);
            try {
                const response = await axios.get(getEventEvaluationUrl(eventId, searchQuery));
                if (response.data) {
                    const filteredData = response.data.filter(
                        (item: Evaluation) => item.type !== 'summary_text_sample'
                    );
                    setSortedEvaluations(filteredData.sort((a: Evaluation, b: Evaluation) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0)));
                } else {
                    console.error('API response data is undefined');
                }
            } catch (error) {
                console.error('Error fetching evaluations:', error);
            } finally {
                setLoading(false);
            }
        }, [eventId, searchQuery, isEvaluationsTabClicked]);

        useEffect(() => {
            fetchEvaluations();
        }, [fetchEvaluations]);

        const getCount = (value: any): number => {
            if (Array.isArray(value)) {
                return value.length;
            }
            return value ? 1 : 0;
        };

        const generateInsightText = (
            type: string,
            channelCompAvg: string,
            channelComp: string,
            percentageSameValue?: number,
            percentageSameTopic?: number
        ): string => {
            if (type === 'numerical_descriptor') {
                const channelCompNum = parseFloat(channelComp);
                const prefix = channelCompNum > 0 ? '+' : channelCompNum < 0 ? '' : '';
                return `${channelCompAvg} ${i18n.t('average.text')}, ${prefix}${channelComp}%`;
            } else if (type === 'list_field' && percentageSameValue !== undefined) {
                return `${percentageSameValue.toFixed(2)}% ${i18n.t('vl_events.text')}`;
            } else if ((type === 'selection_field' || type === 'computed_classification') && percentageSameTopic !== undefined) {
                return `${percentageSameTopic.toFixed(2)}% ${i18n.t('ct_events.text')}`;
            }
            return i18n.t('jt_no_data_available.text');
        };

        const processedEvaluations: ProcessedEvaluation[] = sortedEvaluations.flatMap((x: Evaluation): ProcessedEvaluation[] => {
            let parsedValue: any;
            let channelComp = 0;
            let channelCompAvg = 0;

            if (x.type === 'list_field') {
                try {
                    parsedValue = JSON.parse(x.value);
                } catch (e) {
                    parsedValue = {};
                }
            }

            if (x.channelComp?.avg) {
                channelComp = Number(((x.value / x.channelComp.avg) * 100 - 100).toFixed(2));
                channelCompAvg = Number(x.channelComp.avg.toFixed(2));
            }

            let eventTypeComp = 0;
            let eventTypeCompAvg = 0;
            if (x.eventTypeComp?.avg) {
                eventTypeComp = Number(((x.value / x.eventTypeComp.avg) * 100 - 100).toFixed(2));
                eventTypeCompAvg = Number(x.eventTypeComp.avg.toFixed(2));
            }

            const insight = generateInsightText(
                x.type,
                channelCompAvg.toString(),
                channelComp.toString(),
                x.percentage_same_value,
                x.percentage_same_topic
            );

            if (x.type === 'list_field') {
                const values: ProcessedEvaluation[] = [];
                if (parsedValue.first_level) {
                    values.push({
                        id: `${x.id}-first_level`,
                        label: x.label,
                        value: (
                            <div>
                                <div>{i18n.t('jt_1st_level_evaluation.text')}</div>
                                <div>{Array.isArray(parsedValue.first_level?.name) ? parsedValue.first_level.name.join(', ') : parsedValue.first_level.name || ''}</div>
                                <span className="badge badge-info badge-pill">
                                    {getCount(parsedValue.first_level.name)}
                                </span>
                            </div>
                        ),
                        insight: insight,
                        priority: x.priority || false,
                    });
                    if (parsedValue.first_level.second_level) {
                        values.push({
                            id: `${x.id}-second_level`,
                            label: x.label,
                            value: (
                                <div>
                                    <div>{i18n.t('jt_2nd_level_evaluation.text')}</div>
                                    <div>{Array.isArray(parsedValue.first_level.second_level?.name) ? parsedValue.first_level.second_level.name.join(', ') : parsedValue.first_level.second_level.name || ''}</div>
                                    <span className="badge badge-info badge-pill">
                                        {getCount(parsedValue.first_level.second_level.name)}
                                    </span>
                                </div>
                            ),
                            insight: insight,
                            priority: x.priority || false,
                        });
                        if (parsedValue.first_level.second_level.third_level) {
                            values.push({
                                id: `${x.id}-third_level`,
                                label: x.label,
                                value: (
                                    <div>
                                        <div>{i18n.t('jt_3rd_level_evaluation.text')}</div>
                                        <div>{Array.isArray(parsedValue.first_level.second_level.third_level?.name) ? parsedValue.first_level.second_level.third_level.name.join(', ') : parsedValue.first_level.second_level.third_level.name || ''}</div>
                                        <span className="badge badge-info badge-pill">
                                            {getCount(parsedValue.first_level.second_level.third_level.name)}
                                        </span>
                                    </div>
                                ),
                                insight: insight,
                                priority: x.priority || false,
                            });
                        }
                    }
                }
                return values;
            } else {
                return [{
                    id: x.id,
                    key: x.key,
                    label: x.label,
                    value: x.value,
                    channelAverage: channelCompAvg,
                    channelComp: channelComp,
                    eventTypeAverage: eventTypeCompAvg,
                    eventTypeComp: eventTypeComp,
                    description: x.description,
                    insight: insight,
                    priority: x.priority || false,
                    _restricted: x._restricted,
                }];
            }
        });

        const columns = [
            {
                accessorKey: 'label',
                header: i18n.t('th_evaluation.text'),
                enableSorting: false,
                cell: ({row, cell}: {row: any, cell: any}) => {
                    const label = cell.getValue();
                    const isPriority = row.original.priority;
                    return (
                        <div style={{maxWidth: '300px'}}>
                            <span style={{wordWrap: 'break-word'}}>{label}</span>
                            <span style={{marginLeft: '0.25rem'}}>
                                <Tooltip
                                    content={
                                        row.original.description
                                            ? row.original.description.replace(/<[^>]+>/g, '')
                                            : i18n.t('event_sum_no_desc.text')
                                    }
                                    placement="top"
                                >
                                    <Icon name="mdi-information-outline" size="xs" className="text-info" />
                                </Tooltip>
                            </span>
                            {isPriority && (
                                <span style={{marginLeft: '0.5em'}}>
                                    <PrioritiseButton isPriority={isPriority} />
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'value',
                accessorKey: 'value',
                header: () => (
                    <div className="d-flex align-items-center" style={{justifyContent: 'flex-end', textAlign: 'right'}}>
                        {i18n.t('jt_value.text')}
                    </div>
                ),
                enableSorting: false,
                cell: ({row, cell}: {row: any, cell: any}) => {
                    const value = cell.getValue();
                    if (row.original._restricted) {
                        return (
                            <div style={{textAlign: 'right', marginLeft: 'auto'}}>
                                <RestrictedFieldIndicator />
                            </div>
                        );
                    }
                    return (
                        <div
                            style={{wordWrap: 'break-word', maxWidth: '300px', textAlign: 'right', marginLeft: 'auto'}}>
                            {value}
                        </div>
                    );
                },
            },
            {
                id: 'insight',
                accessorKey: 'insight',
                header: (
                    <div className="d-flex align-items-center" style={{justifyContent: 'flex-end', textAlign: 'right'}}>
                        {i18n.t('jt_insight.text')}
                        <Tooltip
                            content={i18n.t('tt_the_displayed_comarison_results.text', {
                                days: appConfig.EVALUATION_TYPE_BEFORE_X_DAYS_THRESHOLD
                            })}
                            placement="top"
                        >
                            <button
                                type="button"
                                className="btn btn-link p-0 ml-2"
                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                data-cy="event-evaluations-tab-btn">
                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                            </button>
                        </Tooltip>
                    </div>
                ),
                enableSorting: false,
                cell: ({cell}: {cell: any}) => {
                    const insight = cell.getValue();
                    return (
                        <div
                            style={{wordWrap: 'break-word', maxWidth: '300px', textAlign: 'right', marginLeft: 'auto'}}>
                            {insight}
                        </div>
                    );
                },
            },
        ];

        return (
            <Row>
                <div className="w-100 d-flex flex-column flex-sm-row justify-content-between">
                    <Col className="mb-2 d-flex flex-row align-items-center">
                        <Form
                            className="search-box"
                            onSubmit={(e: React.FormEvent) => {
                                e.preventDefault();
                            }}
                        >
                            <Form.Control
                                type="text"
                                placeholder={i18n.t('es_search.text')}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                                data-cy="eval-search-field"
                            />
                        </Form>
                    </Col>
                </div>

                <Col md={12}>
                    <Card className="pt-1 custom-border">
                        {
                            loading ? (
                                <CellLoader fullHeight={true} />
                            ) : (
                                <div className="card">
                                    <div className="card-body">
                                        <TableComponent
                                            data={processedEvaluations}
                                            columns={columns}
                                            customTable={true}
                                            sortable={true}
                                        />
                                    </div>
                                </div>
                            )
                        }
                    </Card>
                </Col>
            </Row>
        );
    },
);

export default EventEvaluationsTab; 